import React from 'react';
import { ErrorFallback } from './ErrorFallback';

/**
 * React Error Boundary that catches render/lifecycle errors in any child tree
 * and renders the standard <ErrorFallback> UI instead of a blank white screen.
 *
 * Props:
 *   fallback – optional custom fallback element/render-prop
 *   onError  – optional callback invoked with (error, errorInfo)
 *
 * Resets its error state when the user clicks "Try again" on the fallback.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Unhandled render error:', error, errorInfo);
    if (typeof this.props.onError === 'function') {
      try {
        this.props.onError(error, errorInfo);
      } catch (_) {
        /* swallow errors in the error handler itself */
      }
    }
  }

  handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const { fallback } = this.props;
    if (typeof fallback === 'function') {
      return fallback({ error, retry: this.handleRetry });
    }
    if (React.isValidElement(fallback)) {
      return fallback;
    }
    return <ErrorFallback error={error} onRetry={this.handleRetry} />;
  }
}

export default ErrorBoundary;
